import type { ShoppingItem, Subscription } from '../db/schema.js';
import { frequencyDays } from './subscription.js';

const DAY_MS = 86_400_000;

export type HealthLevel = 'green' | 'yellow' | 'red';

/** §11.1 — 最後收貨：X 天前 */
export interface LastReceivedMetric {
  daysAgo: number;
  level: HealthLevel;
  /** 從未收過貨；用 subscription createdAt（或第一筆 history.addedAt）算「建立後 X 天」 */
  neverReceived: boolean;
}

/** §11.2 — 準時率（過去 6 期） */
export interface OnTimeMetric {
  ratio: number; // 0–1
  count: number; // 樣本數（≤ 6）
  level: HealthLevel;
}

/** §11.3 — 平均 leadDays 偏離 */
export interface LeadDaysDeltaMetric {
  avgDays: number; // rounded 1 decimal；正 = 偏晚
  count: number;
  /** 'increase' = 建議調大 leadDays / 'decrease' = 調小 / 'ok' = 區間內 */
  recommendation: 'increase' | 'decrease' | 'ok';
}

export interface SubscriptionWarning {
  /** 'long_no_receive' = 長期沒收貨 / 'high_cancel' = 常被取消 */
  kind: 'long_no_receive' | 'high_cancel';
  message: string;
}

export interface SubscriptionHealth {
  lastReceived: LastReceivedMetric;
  onTime?: OnTimeMetric;
  leadDaysDelta?: LeadDaysDeltaMetric;
  warnings: SubscriptionWarning[];
  /** §11.5 整體健康分數（0–5；rounded 1 decimal） */
  rating: number;
}

const RECENT_PERIOD_COUNT = 6;
const ON_TIME_MS = DAY_MS;
const LATE_MS = 3 * DAY_MS;

/** §11 - 從 sub + 該 sub 的 ShoppingItem 歷史推算健康度（不另存欄位）。 */
export function computeSubscriptionHealth(
  sub: Subscription,
  history: ShoppingItem[],
  now: number,
  /** sub.createdAt 從 subscription_created event payload 取；無則用 history 第一筆 addedAt */
  subCreatedAt?: number,
): SubscriptionHealth {
  const cadenceDays = frequencyDays(sub.frequency, sub.customDays);
  const cadenceMs = cadenceDays * DAY_MS;

  // 11.1 lastReceived
  const received = history.filter((r) => r.status === 'received' && r.receivedAt !== undefined);
  let lastReceived: LastReceivedMetric;
  if (received.length === 0) {
    const baseAt =
      subCreatedAt ??
      (history.length > 0 ? Math.min(...history.map((r) => r.addedAt)) : now);
    const daysAgo = Math.floor((now - baseAt) / DAY_MS);
    lastReceived = {
      daysAgo,
      level: classifyLastReceivedLevel(daysAgo, cadenceDays),
      neverReceived: true,
    };
  } else {
    const lastAt = Math.max(...received.map((r) => r.receivedAt!));
    const daysAgo = Math.floor((now - lastAt) / DAY_MS);
    lastReceived = {
      daysAgo,
      level: classifyLastReceivedLevel(daysAgo, cadenceDays),
      neverReceived: false,
    };
  }

  // 11.2 / 11.3 — 用最近 6 筆 received 且帶 expectedArrival 的紀錄
  const dated = received
    .filter((r) => r.expectedArrival !== undefined)
    .sort((a, b) => (b.receivedAt ?? 0) - (a.receivedAt ?? 0))
    .slice(0, RECENT_PERIOD_COUNT);

  let onTime: OnTimeMetric | undefined;
  let leadDaysDelta: LeadDaysDeltaMetric | undefined;
  if (dated.length >= 3) {
    let scoreSum = 0;
    let deltaSum = 0;
    for (const r of dated) {
      const delta = (r.receivedAt ?? 0) - (r.expectedArrival ?? 0);
      const abs = Math.abs(delta);
      let s = 0;
      if (abs <= ON_TIME_MS) s = 1;
      else if (abs <= LATE_MS) s = 0.5;
      scoreSum += s;
      deltaSum += delta / DAY_MS;
    }
    const ratio = scoreSum / dated.length;
    onTime = {
      ratio,
      count: dated.length,
      level: ratio >= 0.85 ? 'green' : ratio >= 0.5 ? 'yellow' : 'red',
    };
    const avgDays = Math.round((deltaSum / dated.length) * 10) / 10;
    leadDaysDelta = {
      avgDays,
      count: dated.length,
      recommendation: avgDays > 2 ? 'increase' : avgDays < -2 ? 'decrease' : 'ok',
    };
  }

  // 11.4 警示 banner
  const warnings: SubscriptionWarning[] = [];

  // 常 cancel ratio：取最近 6 筆任何 status 的紀錄
  const recentAny = [...history]
    .sort((a, b) => b.addedAt - a.addedAt)
    .slice(0, RECENT_PERIOD_COUNT);
  const cancelCount = recentAny.filter((r) => r.status === 'cancelled').length;
  if (recentAny.length >= 4 && cancelCount >= 3) {
    warnings.push({
      kind: 'high_cancel',
      message: '這個訂閱常被取消，要不要調整數量或頻率？',
    });
  }

  // 長期沒收貨：lastReceived > 2 × cadenceDays（且不是剛建立）
  if (!lastReceived.neverReceived && lastReceived.daysAgo > 2 * cadenceDays) {
    warnings.push({
      kind: 'long_no_receive',
      message: '這個訂閱還在跑嗎？',
    });
  }

  // 11.5 整體健康分數（0–5）
  let score = 0;
  score += lastReceived.level === 'green' ? 2 : lastReceived.level === 'yellow' ? 1 : 0;
  if (onTime === undefined) {
    score += 1; // 中性 +1
  } else {
    score += onTime.level === 'green' ? 2 : onTime.level === 'yellow' ? 1 : 0;
  }
  if (leadDaysDelta === undefined) {
    score += 0.5; // 中性 +0.5
  } else {
    score += Math.abs(leadDaysDelta.avgDays) <= 2 ? 1 : 0;
  }
  const rating = Math.min(5, Math.round(score * 10) / 10);

  const result: SubscriptionHealth = {
    lastReceived,
    warnings,
    rating,
  };
  if (onTime) result.onTime = onTime;
  if (leadDaysDelta) result.leadDaysDelta = leadDaysDelta;
  return result;
}

function classifyLastReceivedLevel(daysAgo: number, cadenceDays: number): HealthLevel {
  if (daysAgo < cadenceDays) return 'green';
  if (daysAgo < 1.5 * cadenceDays) return 'yellow';
  return 'red';
}
